import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";
import { BACKEND } from "@/lib/api/_shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Truck } from "lucide-react";

/**
 * Password reset / invite landing page.
 * Scanner-safe links (?token_hash=…&type=recovery) are only exchanged when the
 * person clicks "Continue" — email link-scanners (e.g. M365 Safe Links) only
 * GET the page, so they can't burn the one-time token. Legacy hash links still work.
 */
const urlParams = () => new URLSearchParams(window.location.search);
const hashError = () => new URLSearchParams(window.location.hash.slice(1)).get("error_description");
async function completeForcedChange(password) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return { message: "Please sign in again." };
  const res = await fetch(`${BACKEND}/api/auth/complete-password-change`, {
    method: "POST",
    headers: { Authorization: `Bearer ${session.access_token}`, "Content-Type": "application/json" },
    body: JSON.stringify({ new_password: password }),
  });
  if (!res.ok) return { message: (await res.json().catch(() => ({}))).detail || "Unable to change password" };
  // The password change ends the old session server-side — sign in with the new one.
  const { error } = await supabase.auth.signInWithPassword({ email: session.user.email, password });
  return error ? { message: "Password saved — please sign in with your new password." } : null;
}

export default function ResetPassword() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [hasSession, setHasSession] = useState(false);
  const [tokenHash] = useState(() => urlParams().get("token_hash"));
  const [verifying, setVerifying] = useState(false);
  const [forced, setForced] = useState(null); // admin-set temp password → { expired }
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (tokenHash) { setReady(true); return undefined; }
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => { if (s) { setHasSession(true); setError(null); } });
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      const meta = session?.user?.app_metadata || {};
      if (meta.must_change_password === true) {
        const exp = Date.parse(meta.temp_password_expires_at || "");
        if (!Number.isFinite(exp) || Date.now() >= exp) {
          await supabase.auth.signOut({ scope: "local" });
          setForced({ expired: true });
          setError("Your temporary password has expired. Ask your administrator for a new one.");
          setReady(true);
          return;
        }
        setForced({ expired: false });
      }
      setReady(true);
      setHasSession(!!session);
      if (!session) {
        setError(`${hashError() ? `${hashError()}. ` : ""}This link has already been used or has expired. Request a new one below.`);
      }
    });
    return () => sub.subscription.unsubscribe();
  }, [tokenHash]);

  const verify = async () => {
    setVerifying(true);
    setError(null);
    const type = urlParams().get("type") || "recovery";
    const { data, error: err } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type });
    setVerifying(false);
    window.history.replaceState(null, "", window.location.pathname);
    if (err || !data?.session) {
      setError("This link has already been used or has expired. Request a new one below.");
    } else {
      setHasSession(true);
    }
    setReadyToken(false);
  };
  const [readyToken, setReadyToken] = useState(true);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    const err = forced ? await completeForcedChange(password) : (await supabase.auth.updateUser({ password })).error;
    setSubmitting(false);
    if (err) {
      setError(err.message);
      return;
    }
    setDone(true);
    setTimeout(() => navigate("/", { replace: true }), 1500);
  };

  return (
    <div
      data-testid="reset-password-page"
      className="min-h-screen w-full bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 flex items-center justify-center px-4 py-12"
    >
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="rounded-xl bg-white/10 backdrop-blur p-3 ring-1 ring-white/20">
            <Truck className="h-7 w-7 text-white" strokeWidth={1.5} />
          </div>
          <div className="text-white">
            <div className="text-xl font-semibold tracking-tight">timeslot.au</div>
            <div className="text-xs text-white/70 -mt-0.5">Logistics Booking System</div>
          </div>
        </div>

        <Card className="border-white/10 bg-white/95 backdrop-blur shadow-2xl">
          <CardHeader>
            <CardTitle>Set new password</CardTitle>
            <CardDescription>
              {forced && !forced.expired ? "You signed in with a temporary password. Choose your own password (8+ characters) to continue." : "Choose a strong password (8+ characters)."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!ready ? (
              <div className="text-sm text-muted-foreground">Verifying link…</div>
            ) : done ? (
              <Alert data-testid="reset-done-alert">
                <AlertDescription>Password updated. Redirecting…</AlertDescription>
              </Alert>
            ) : tokenHash && readyToken ? (
              <div className="space-y-3">
                <p className="text-sm text-slate-600">Click continue to verify your link and set your password.</p>
                <Button className="w-full" onClick={verify} disabled={verifying} data-testid="reset-continue-btn">
                  {verifying ? "Verifying…" : "Continue"}
                </Button>
              </div>
            ) : !hasSession ? (
              <div className="space-y-3">
                <Alert variant="destructive" data-testid="reset-link-error">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
                {!forced && (
                  <Button asChild variant="outline" className="w-full" data-testid="reset-request-new-btn">
                    <Link to="/forgot-password">Request a new link</Link>
                  </Button>
                )}
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                <div className="space-y-2">
                  <Label htmlFor="pw">New password</Label>
                  <Input
                    id="pw"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="reset-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pw2">Confirm password</Label>
                  <Input
                    id="pw2"
                    type="password"
                    required
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    data-testid="reset-password-confirm-input"
                  />
                </div>
                <Button type="submit" className="w-full" disabled={submitting} data-testid="reset-submit-btn">
                  {submitting ? "Saving…" : "Update password"}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
